import { useEffect, useRef } from "react";
import { IconPlus, IconX } from "@tabler/icons-react";
import { useFieldArray } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { useLanguage } from "../../../context/LanguageContext";

export default function EditRackModal({
  open,
  onClose,
  register,
  setValue,
  control,
  errors,
  handleSubmit,
  isPending,
  defaultValues,
  inline,
}: any) {
  const { t } = useLanguage();
  const { fields, replace, append } = useFieldArray({
    control,
    name: "levels",
  });

  const levels = useWatch({
    control,
    name: "levels",
  });

  const prevDefaultValuesRef = useRef<any>(null);

  // Prefill
  useEffect(() => {
    if (defaultValues && open) {
      // Only update if defaultValues has actually changed
      const currentValuesStr = JSON.stringify({
        name: defaultValues?.name,
        levels: defaultValues?.levels?.map((lvl: any) => ({
          id: lvl.id,
          name: lvl.name,
          levelNumber: lvl.levelNumber,
        })) || []
      });

      if (currentValuesStr !== prevDefaultValuesRef.current) {
        setValue("rackName", defaultValues?.name || "");

        // set levels array - handle both levels and level (singular) properties
        const levelData =
          defaultValues?.levels?.map((lvl: any) => ({
            id: lvl.id, // MUST
            name: lvl.name,
            levelNumber: lvl.levelNumber,
          })) ||
          defaultValues?.level?.map((lvl: any) => ({
            id: lvl.id, // MUST
            name: lvl.name,
            levelNumber: lvl.levelNumber,
          })) || [];

        replace(levelData);
        prevDefaultValuesRef.current = currentValuesStr;
      }
    }
  }, [defaultValues, open, setValue, replace]);

  if (!open) return null;

  const handleAddLevel = () => {
    if (fields.length < 8) {
      append({
        name: "",
        levelNumber: fields.length + 1
      });
    }
  };

  const hasEmptyLevel = levels?.some(
    (lvl: any, index: number) => (!lvl?.name || lvl.name.trim() === "") && index < levels.length - 1
  );


  const content = (
    <div className={`bg-[#F7F6F2] rounded-3xl ${inline ? "" : "w-[500px]"} flex flex-col`}>
      {/* Header */}
      <div className="flex justify-between px-4 py-3 border-b">
        <h2 className="text-lg font-medium text-[#212121]">
          {inline ? t("Add More Levels") : t("Edit Rack")}
        </h2>
        <button onClick={onClose}>
          <IconX />
        </button>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Rack */}
        {!inline && (
          <div>
            <label className="text-sm text-[#1E293B] block mb-1">{t("Rack Name")}</label>
            <input
              {...register("rackName", { required: true, maxLength: 3 })}
              maxLength={3}
              className="w-full border rounded-full px-4 py-2 text-sm bg-white"
            />
          </div>
        )}

        {/* Levels */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-medium text-[#1E293B]">{t("Levels")}</h2>
            <button
              type="button"
              onClick={handleAddLevel}
              disabled={fields.length >= 8 || hasEmptyLevel}
              className={`flex items-center justify-center w-10 h-10 rounded-full border 
              ${fields.length >= 8 || hasEmptyLevel
                  ? "opacity-50 cursor-not-allowed"
                  : "bg-white hover:shadow"
                }`}
            >
              <IconPlus stroke={1.5} size={20} />
            </button>
          </div>

          {fields.map((field, index) => {
            const isExistingLevel = index < (defaultValues?.levels?.length || defaultValues?.level?.length || 0);
            return (
              <input
                key={field.id}
                {...register(`levels.${index}.name`, isExistingLevel ? {} : { maxLength: 3 })}
                maxLength={isExistingLevel ? undefined : 3}
                disabled={isExistingLevel}
                className={`w-full border rounded-full px-4 py-2 text-sm ${isExistingLevel
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "bg-white text-[#212121] border-gray-300"
                  }`}
                placeholder={`${t("Level")} ${index + 1}`}
              />
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end p-4 border-t">
        <button
          onClick={handleSubmit}
          disabled={isPending || hasEmptyLevel}
          className="px-4 py-2 rounded-full bg-[#FBD037] text-black font-medium hover:bg-yellow-500 transition disabled:opacity-50"
        >
          {isPending ? t("Updating...") : t("Update")}
        </button>
      </div>
    </div>
  );

  if (inline) return content;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      {content}
    </div>
  );
}
